"use client";

import clsx from "clsx";
import React from "react";
import { InplaceVideo } from "./InplaceVideo";
import css from "./video.module.scss";

interface VideoProps extends React.ComponentProps<typeof InplaceVideo>
{
    allowControls?: boolean;
    aspecRatio?: number;
    sourceType?: string;
    wrapperProps?: React.ComponentProps<"div">;
}

export const Video = ({ allowControls, aspecRatio, sourceType, src, wrapperProps: { className: wrapperClassName, ...wrapperProps } = {}, ...props }: VideoProps): React.ReactComponentElement<"video", VideoProps> =>
{
    const controls = allowControls ?
        <>
            <button data-video-play type={"button"} title="Play">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="30" cy="30" r="30" fill="#fff" />
                    <path d="M27.0765 38.0765L27.0765 30.1005L27.0765 22.1247L38.8995 30.1005L27.0765 38.0765Z" fill="#414042" />
                </svg>
            </button>
            <button data-video-pause type={"button"} title="Pause">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="30" cy="30" r="30" fill="#fff" />
                    <rect x="32" y="23" width="4" height="14" fill="#414042" />
                    <rect x="24" y="23" width="4" height="14" fill="#414042" />
                </svg>
            </button>
            <button data-video-replay type={"button"} title="Replay">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="30" cy="30" r="30" fill="#fff" />
                    <path d="M29.062 37.885c3.644 0 6.718-2.485 7.618-5.849h2.144L35.542 26.353 32.262 32.036h1.884c-.809 2.015-2.783 3.442-5.084 3.442-3.02 0-5.478-2.457-5.478-5.478 0-3.02 2.457-5.478 5.478-5.478 1.554 0 2.957.651 3.955 1.693l1.237-2.143c-1.388-1.218-3.204-1.958-5.192-1.958C24.714 22.115 21.177 25.652 21.177 30c0 4.348 3.536 7.885 7.885 7.885z" fill="#414042" />
                </svg>
            </button>
        </>
        :
        null;

    // Ensure `<source src={…}/>` always receives a string.
    const sourceSrc = React.useMemo(() => {
      if (!src) return undefined;
      if (typeof src === 'string') return src;

      // MediaStream cannot be converted to an object‑URL that <source> understands;
      // let the caller handle that via <video srcObject={...}/> instead.
      if (src instanceof MediaStream) return undefined;

      // Blob | MediaSource → object URL
      return URL.createObjectURL(src as Blob | MediaSource);
    }, [src]);

    // Revoke the object URL when `src` changes or component unmounts
    React.useEffect(() => {
      return () => {
        if (sourceSrc && sourceSrc.startsWith('blob:')) {
          URL.revokeObjectURL(sourceSrc);
        }
      };
    }, [sourceSrc]);

    return (
        <div {...wrapperProps} className={clsx(css["wrapper"], wrapperClassName)} data-logic-feature="video-controls">
            <InplaceVideo {...props}>
                <source src={sourceSrc} type={sourceType} />
            </InplaceVideo>
            {controls}
        </div >
    );
}
